import { useEffect, useState } from "react";
import { api, formatPrice } from "@/lib/api";
import { toast } from "sonner";
import { JerseyBackPreview } from "@/components/JerseyBackPreview";
import { Loader2, Plus, Trash2, RefreshCw, CheckCircle2, XCircle, LogOut, RotateCcw, Search, Send, Mail, Package, CalendarClock, Tag, Archive, ArchiveRestore, Gift, PackageX, FlaskConical, Download, ShoppingBag } from "lucide-react";

export default function AdminPage() {
  const [categories, setCategories] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [config, setConfig] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [refunding, setRefunding] = useState(null);
  const [resending, setResending] = useState(null);
  const [fulfilling, setFulfilling] = useState(null);
  const [orderSearch, setOrderSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("paid");
  const [dateRange, setDateRange] = useState("all");
  const [products, setProducts] = useState([]);
  const [subCount, setSubCount] = useState(0);
  const [drop, setDrop] = useState({ product_id: "", title: "", message: "", image_url: "" });
  const [sending, setSending] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [deletingOrder, setDeletingOrder] = useState(null);
  const [archiving, setArchiving] = useState(null);
  const [testMode, setTestMode] = useState(false);
  const [togglingTest, setTogglingTest] = useState(false);
  const [purgingTests, setPurgingTests] = useState(false);

  const purgeTestOrders = async () => {
    if (!window.confirm("Permanently delete ALL test orders placed while in Test Mode? This can't be undone.")) return;
    setPurgingTests(true);
    try {
      const r = await api.post("/admin/orders/purge-tests");
      toast.success(`Deleted ${r.data.deleted} test order(s).`);
      loadOrders();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete test orders.");
    } finally {
      setPurgingTests(false);
    }
  };

  const toggleTestMode = async () => {
    const next = !testMode;
    if (next && !window.confirm("Turn ON Test Mode? Real PayPal charges and Printify production orders will be BLOCKED store-wide until you turn it off.")) return;
    setTogglingTest(true);
    try {
      const r = await api.post("/admin/settings", { test_mode: next });
      setTestMode(!!r.data.test_mode);
      toast.success(r.data.test_mode ? "Test Mode ON — safe to verify, no real charges." : "Test Mode OFF — store is live.");
    } catch {
      toast.error("Could not update Test Mode.");
    } finally {
      setTogglingTest(false);
    }
  };

  const archiveTestOrders = async () => {
    if (!window.confirm("Archive all test, abandoned and refunded orders? They'll be hidden from your list but can be restored anytime from the Archived tab.")) return;
    setCleaning(true);
    try {
      const r = await api.post("/admin/orders/archive-test");
      toast.success(`Archived ${r.data.archived} test/refunded order(s). View them under the Archived tab.`);
      loadOrders();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Archive failed.");
    } finally {
      setCleaning(false);
    }
  };

  const archiveOrder = async (id) => {
    setArchiving(id);
    try {
      await api.post(`/admin/orders/${id}/archive`);
      toast.success("Order archived. Find it under the Archived tab.");
      loadOrders();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not archive the order.");
    } finally {
      setArchiving(null);
    }
  };

  const restoreOrder = async (id) => {
    setArchiving(id);
    try {
      await api.post(`/admin/orders/${id}/unarchive`);
      toast.success("Order restored.");
      loadOrders();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not restore the order.");
    } finally {
      setArchiving(null);
    }
  };

  const deleteOrder = async (id) => {
    if (!window.confirm("Permanently delete this order from your store? This can't be undone.")) return;
    setDeletingOrder(id);
    try {
      await api.delete(`/admin/orders/${id}`);
      toast.success("Order deleted.");
      loadOrders();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete the order.");
    } finally {
      setDeletingOrder(null);
    }
  };
  const [sales, setSales] = useState([]);
  const [salesLoading, setSalesLoading] = useState(true);
  const [newSale, setNewSale] = useState({ name: "", discount_dollars: "5", start: "", end: "" });
  const [creatingSale, setCreatingSale] = useState(false);
  const [deletingSale, setDeletingSale] = useState(null);
  const [downloadingTax, setDownloadingTax] = useState(false);
  const [etsy, setEtsy] = useState(null);
  const [etsyBusy, setEtsyBusy] = useState(false);

  const loadEtsy = () => api.get("/admin/etsy/status").then((r) => setEtsy(r.data)).catch(() => {});
  useEffect(() => {
    loadEtsy();
    const t = setInterval(loadEtsy, 8000);
    return () => clearInterval(t);
  }, []);

  const etsyTest = async () => {
    setEtsyBusy(true);
    try {
      const r = await api.post("/admin/etsy/publish-test");
      toast.success(`Test listing published: ${r.data.title}`);
      loadEtsy();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test publish failed.");
    } finally {
      setEtsyBusy(false);
    }
  };

  const etsyPublishAll = async () => {
    const n = etsy?.total_listings_if_all || 0;
    if (!window.confirm(`Publish ALL designs to your LIVE eBay shop?\n\nThis creates ~${n} listings (Hoodie + Shirt per design). eBay gives most sellers 250 free-insertion listings/month, so listing is usually $0 — you pay eBay's final-value fee only when an item sells. Runs in the background over ~45+ min due to publish limits.\n\nRequires your eBay↔Printify setup to be complete (see the note in this panel).`)) return;
    setEtsyBusy(true);
    try {
      await api.post("/admin/etsy/publish-all");
      toast.success("Publishing started — this runs in the background.");
      loadEtsy();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start publishing.");
    } finally {
      setEtsyBusy(false);
    }
  };

  const downloadTaxCSV = async () => {
    setDownloadingTax(true);
    try {
      const r = await api.get(`/admin/tax-report.csv?range=${dateRange}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `jerseyblend-tax-${dateRange}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the tax report.");
    } finally {
      setDownloadingTax(false);
    }
  };

  const loadSales = () => {
    setSalesLoading(true);
    return api.get("/admin/sales")
      .then((r) => setSales(r.data))
      .catch((err) => console.error("Failed to load sales:", err))
      .finally(() => setSalesLoading(false));
  };

  const createSale = async (e) => {
    e.preventDefault();
    const dollars = parseFloat(newSale.discount_dollars);
    if (!newSale.name.trim()) return toast.error("Give the sale a name.");
    if (!dollars || dollars <= 0) return toast.error("Discount must be greater than $0.");
    if (!newSale.start || !newSale.end) return toast.error("Pick a start and end date.");
    if (newSale.end < newSale.start) return toast.error("End date must be on or after the start date.");
    setCreatingSale(true);
    try {
      await api.post("/admin/sales", {
        name: newSale.name.trim(),
        discount_cents: Math.round(dollars * 100),
        start: newSale.start,
        end: newSale.end,
      });
      toast.success("Sale scheduled.");
      setNewSale({ name: "", discount_dollars: "5", start: "", end: "" });
      loadSales();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not schedule the sale.");
    } finally {
      setCreatingSale(false);
    }
  };

  const deleteSale = async (id) => {
    if (!window.confirm("Delete this scheduled sale? If it's active now, prices return to full.")) return;
    setDeletingSale(id);
    try {
      await api.delete(`/admin/sales/${id}`);
      toast.success("Sale removed.");
      loadSales();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete the sale.");
    } finally {
      setDeletingSale(null);
    }
  };

  const reconcileNow = async () => {
    setReconciling(true);
    try {
      const r = await api.post("/admin/reconcile");
      if (r.data.recovered > 0) {
        toast.success(`Reconcile complete — recovered ${r.data.recovered} stuck order(s).`);
      } else if (r.data.unfulfilled_after > 0) {
        toast.warning(`${r.data.unfulfilled_after} order(s) still pending — Printify may still be calculating. Try again shortly.`);
      } else {
        toast.success("All caught up — no stuck orders.");
      }
      loadOrders();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reconcile failed.");
    } finally {
      setReconciling(false);
    }
  };

  const [sample, setSample] = useState({
    product_id: "", garment: "", size: "", custom_name: "", custom_number: "", quantity: 1,
    first_name: "", last_name: "", email: "", phone: "", address1: "", address2: "",
    city: "", region: "", zip: "", country: "US",
  });
  const [placingSample, setPlacingSample] = useState(false);

  const sampleProduct = products.find((p) => p.id === sample.product_id);
  const sampleGarments = sampleProduct
    ? [...new Set(sampleProduct.variants?.filter((v) => v.is_enabled).map((v) => v.options?.garment).filter(Boolean))]
    : [];
  const sampleSizes = sampleProduct && sample.garment
    ? sampleProduct.variants.filter((v) => v.is_enabled && v.options?.garment === sample.garment).map((v) => v.options?.size)
    : [];

  const placeSampleOrder = async () => {
    if (!sampleProduct) return toast.error("Pick a product.");
    const variant = sampleProduct.variants.find(
      (v) => v.is_enabled && v.options?.garment === sample.garment && v.options?.size === sample.size);
    if (!variant) return toast.error("Pick a garment and size.");
    for (const [k, label] of [["first_name", "first name"], ["last_name", "last name"], ["email", "email"],
      ["address1", "address"], ["city", "city"], ["region", "state/region"], ["zip", "ZIP"]]) {
      if (!sample[k]?.trim()) return toast.error(`Enter the shipping ${label}.`);
    }
    if (!window.confirm(`Place a REAL Printify production order for a ${sample.garment} (${sample.size})? This will print & ship and charge your Printify account.`)) return;
    setPlacingSample(true);
    try {
      const r = await api.post("/admin/sample-order", {
        product_id: sample.product_id, variant_id: variant.id,
        custom_name: sample.custom_name, custom_number: sample.custom_number,
        quantity: Number(sample.quantity) || 1,
        first_name: sample.first_name, last_name: sample.last_name, email: sample.email,
        phone: sample.phone, address1: sample.address1, address2: sample.address2,
        city: sample.city, region: sample.region, zip: sample.zip, country: sample.country,
      });
      toast.success(`Order placed! ${r.data.sent_to_production ? "Sent to production." : "Created — production will kick off shortly."}`);
      setSample((s) => ({ ...s, custom_name: "", custom_number: "", quantity: 1 }));
      loadOrders();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not place the order.");
    } finally {
      setPlacingSample(false);
    }
  };


  const loadCats = () => api.get("/categories").then((r) => setCategories(r.data));
  const loadOrders = () => {
    setOrdersLoading(true);
    return api.get("/admin/orders")
      .then((r) => setOrders(r.data))
      .catch((err) => console.error("Failed to load orders:", err))
      .finally(() => setOrdersLoading(false));
  };

  useEffect(() => {
    loadCats();
    loadOrders();
    api.get("/config").then((r) => setConfig(r.data));
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/newsletter/count").then((r) => setSubCount(r.data.count)).catch(() => {});
    api.get("/admin/settings").then((r) => setTestMode(!!r.data.test_mode)).catch(() => {});
    loadSales();
  }, []);

  const selectDropProduct = (pid) => {
    const p = products.find((x) => x.id === pid);
    setDrop((d) => ({
      ...d,
      product_id: pid,
      title: p ? p.title : d.title,
      image_url: p?.images?.[0] || "",
    }));
  };

  const sendDrop = async () => {
    if (!drop.title.trim()) return toast.error("Add a title for the drop.");
    setSending(true);
    try {
      const r = await api.post("/admin/announce-drop", drop);
      toast.success(`Announcement sent to ${r.data.sent} of ${r.data.total} subscribers.`);
      setDrop({ product_id: "", title: "", message: "", image_url: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send announcement.");
    } finally {
      setSending(false);
    }
  };

  const fulfillOrder = async (id) => {
    setFulfilling(id);
    try {
      const r = await api.post(`/admin/orders/${id}/fulfill`);
      toast.success(`Order sent to Printify (${r.data.printify_order_id}).`);
      loadOrders();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fulfillment failed.");
    } finally {
      setFulfilling(null);
    }
  };

  const resendEmail = async (id) => {
    setResending(id);
    try {
      const r = await api.post(`/admin/orders/${id}/resend-confirmation`);
      toast.success(`Confirmation re-sent to ${r.data.email}.`);
      loadOrders();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend the email.");
    } finally {
      setResending(null);
    }
  };

  const refundOrder = async (id) => {    if (!window.confirm("Refund this order via PayPal and cancel its Printify order? This cannot be undone.")) return;
    setRefunding(id);
    try {
      const r = await api.post(`/admin/orders/${id}/refund`);
      if (r.data.printify_order_id && !r.data.printify_cancelled && r.data.printify_error) {
        toast.warning("Refunded via PayPal, but Printify cancel failed — cancel it manually.");
      } else {
        toast.success("Order refunded" + (r.data.printify_cancelled ? " and Printify order cancelled." : "."));
      }
      loadOrders();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refund failed.");
    } finally {
      setRefunding(null);
    }
  };

  const approveReturn = async (id) => {
    if (!window.confirm("Refund this order via PayPal for the return? Only do this once you've verified the damage/defect (a customer photo).")) return;
    setRefunding(id);
    try {
      await api.post(`/admin/orders/${id}/return/approve`);
      toast.success("Return approved and customer refunded.");
      loadOrders();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not approve return.");
    } finally {
      setRefunding(null);
    }
  };

  const replaceReturn = async (id) => {
    if (!window.confirm("Send a FREE replacement to the customer? This creates a new $0 Printify order to the same address.")) return;
    setRefunding(id);
    try {
      await api.post(`/admin/orders/${id}/return/replace`);
      toast.success("Free replacement submitted to Printify — customer notified.");
      loadOrders();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create replacement.");
    } finally {
      setRefunding(null);
    }
  };

  const denyReturn = async (id) => {
    const note = window.prompt("Optional note to the customer explaining why (leave blank for a default message):", "");
    if (note === null) return;
    setRefunding(id);
    try {
      await api.post(`/admin/orders/${id}/return/deny`, { note: note.trim() });
      toast.success("Return request declined — customer notified.");
      loadOrders();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not decline return.");
    } finally {
      setRefunding(null);
    }
  };

  const addCat = async (e) => {
    e.preventDefault();
    if (!newCat.trim()) return;
    try {
      await api.post("/categories", { name: newCat.trim() });
      setNewCat("");
      toast.success("Category added.");
      loadCats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add category.");
    }
  };

  const delCat = async (id) => {
    await api.delete(`/categories/${id}`);
    toast.success("Category removed.");
    loadCats();
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/products/sync");
      toast.success(`Synced ${r.data.synced} products from Printify.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed.");
    } finally {
      setSyncing(false);
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    window.location.href = "/";
  };

  const isRefunded = (o) => o.status === "refunded" || o.payment_status === "refunded";
  const isPaid = (o) => o.payment_status === "paid" && !isRefunded(o);
  const isShipped = (o) => o.status === "shipped" || o.status === "delivered";
  const isArchived = (o) => !!o.archived;
  const isOpenClaim = (o) => o.return_status === "requested";

  const matchesStatus = (o) => {
    if (statusFilter === "archived") return isArchived(o);
    if (isArchived(o)) return false; // archived orders only show under the Archived tab
    if (statusFilter === "claims") return isOpenClaim(o);
    if (statusFilter === "paid") return isPaid(o);
    if (statusFilter === "refunded") return isRefunded(o);
    if (statusFilter === "shipped") return isShipped(o);
    return true; // "all" = all non-archived
  };

  const q = orderSearch.trim().toLowerCase();
  const filteredOrders = orders.filter((o) => {
    if (!matchesStatus(o)) return false;
    if (!q) return true;
    return [o.id, o.customer, o.email, o.printify_order_id]
      .filter(Boolean)
      .some((f) => f.toLowerCase().includes(q));
  });

  const activeOrders = orders.filter((o) => !isArchived(o));
  const paidOrders = activeOrders.filter(isPaid);
  const refundedCount = activeOrders.filter(isRefunded).length;
  const archivedCount = orders.filter(isArchived).length;

  // Sales snapshot scoped by date range
  const rangeStart = (() => {
    const now = new Date();
    if (dateRange === "month") return new Date(now.getFullYear(), now.getMonth(), 1);
    if (dateRange === "week") {
      const d = new Date(now);
      const day = (d.getDay() + 6) % 7; // Monday = 0
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() - day);
      return d;
    }
    return null;
  })();
  const inRange = (o) => {
    if (!rangeStart) return true;
    const c = o.created_at ? new Date(o.created_at) : null;
    return c && !isNaN(c) && c >= rangeStart;
  };
  const rangeOrders = activeOrders.filter(inRange);
  const rangePaid = rangeOrders.filter(isPaid);
  const snapRevenue = rangePaid.reduce((sum, o) => sum + (o.total_price || 0), 0);
  const snapTax = rangePaid.reduce((sum, o) => sum + (o.tax_price || 0), 0);
  const snapRefunded = rangeOrders.filter(isRefunded).length;

  const DATE_RANGES = [
    { key: "all", label: "All time" },
    { key: "month", label: "This month" },
    { key: "week", label: "This week" },
  ];

  const openClaimCount = activeOrders.filter(isOpenClaim).length;
  const STATUS_TABS = [
    { key: "all", label: "All", count: activeOrders.length },
    { key: "paid", label: "Paid", count: paidOrders.length },
    { key: "claims", label: "Claims", count: openClaimCount },
    { key: "shipped", label: "Shipped", count: activeOrders.filter(isShipped).length },
    { key: "refunded", label: "Refunded", count: refundedCount },
    { key: "archived", label: "Archived", count: archivedCount },
  ];

  return (
    <div data-testid="admin-page" className="max-w-4xl mx-auto px-6 md:px-12 py-16 bg-background min-h-screen">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-anton text-5xl uppercase tracking-tight text-white">Store Admin</h1>
          <p className="text-white/50 mt-2">Manage categories and sync your Printify catalog.</p>
        </div>
        <button
          data-testid="logout-button"
          onClick={logout}
          className="flex items-center gap-2 border border-white/20 text-white/70 hover:border-primary hover:text-primary px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] transition-colors"
        >
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </div>

      {/* Test / Sample Mode */}
      <div
        data-testid="test-mode-card"
        className={`mt-10 border p-6 flex items-center justify-between flex-wrap gap-4 transition-colors ${
          testMode ? "border-primary bg-primary/10" : "border-white/10 bg-card"
        }`}
      >
        <div className="flex items-center gap-3">
          <FlaskConical className={`w-6 h-6 ${testMode ? "text-primary" : "text-white/40"}`} />
          <div>
            <p className="font-bold uppercase tracking-tight text-white flex items-center gap-2" data-testid="test-mode-status">
              Test Mode {testMode ? "ON" : "OFF"}
              {testMode && (
                <span className="text-[10px] bg-primary text-black px-2 py-0.5 font-bold tracking-[0.2em]">SAFE</span>
              )}
            </p>
            <p className="text-xs text-white/50 max-w-md mt-0.5">
              {testMode
                ? "Checkouts are simulated — no real PayPal charge and nothing is sent to Printify production. Turn off to go live."
                : "Store is LIVE. Turn on to safely verify checkout & fulfillment without real charges or production orders."}
            </p>
            <button
              data-testid="purge-tests-button"
              onClick={purgeTestOrders}
              disabled={purgingTests}
              className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] border border-white/20 text-white/60 hover:border-red-500/60 hover:text-red-400 px-3 py-1.5 transition-colors disabled:opacity-50"
            >
              {purgingTests ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
              Delete all test orders
            </button>
          </div>
        </div>
        <button
          data-testid="test-mode-toggle"
          onClick={toggleTestMode}
          disabled={togglingTest}
          className={`relative inline-flex h-8 w-16 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
            testMode ? "bg-primary" : "bg-white/20"
          }`}
          role="switch"
          aria-checked={testMode}
        >
          <span
            className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
              testMode ? "translate-x-9" : "translate-x-1"
            }`}
          />
        </button>
      </div>

      {/* Sales dashboard (top of page) */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 font-bold">Sales snapshot</p>
          <div className="flex gap-2" data-testid="date-range-filter">
            {DATE_RANGES.map((r) => (
              <button
                key={r.key}
                data-testid={`date-range-${r.key}`}
                onClick={() => setDateRange(r.key)}
                className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] border transition-colors ${
                  dateRange === r.key
                    ? "bg-white text-black border-white"
                    : "bg-transparent text-white/60 border-white/15 hover:border-white/50 hover:text-white"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="sales-snapshot">
          <div className="border border-white/10 bg-card p-5">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold">Revenue</p>
            <p className="font-anton text-3xl text-primary mt-2" data-testid="stat-revenue">{formatPrice(snapRevenue)}</p>
          </div>
          <div className="border border-white/10 bg-card p-5">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold">Paid Orders</p>
            <p className="font-anton text-3xl text-white mt-2" data-testid="stat-orders">{rangePaid.length}</p>
          </div>
          <div className="border border-white/10 bg-card p-5">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold">MD Tax Collected</p>
            <p className="font-anton text-3xl text-white mt-2" data-testid="stat-tax">{formatPrice(snapTax)}</p>
            <button
              data-testid="download-tax-csv"
              onClick={downloadTaxCSV}
              disabled={downloadingTax || snapTax === 0}
              className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] border border-white/20 text-white/60 hover:border-primary hover:text-primary px-2.5 py-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {downloadingTax ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
              Export CSV
            </button>
          </div>
          <div className="border border-white/10 bg-card p-5">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold">Refunded</p>
            <p className="font-anton text-3xl text-white mt-2" data-testid="stat-refunded">{snapRefunded}</p>
          </div>
        </div>
      </div>

      {/* Printify status */}
      <div className="mt-10 border border-white/10 bg-card p-6 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          {config?.printify_connected ? (
            <CheckCircle2 className="w-6 h-6 text-primary" />
          ) : (
            <XCircle className="w-6 h-6 text-red-500" />
          )}
          <div>
            <p className="font-bold uppercase tracking-tight text-white" data-testid="printify-status">
              Printify {config?.printify_connected ? "Connected" : "Not Connected"}
            </p>
            <p className="text-xs text-white/50">
              {config?.printify_connected
                ? "Pull live products from your shop."
                : "Add PRINTIFY_API_TOKEN to backend .env to connect."}
            </p>
          </div>
        </div>
        <button
          data-testid="sync-button"
          onClick={sync}
          disabled={!config?.printify_connected || syncing}
          className="bg-primary text-black hover:bg-white px-6 py-3 font-bold uppercase tracking-[0.2em] text-sm transition-colors flex items-center gap-2 disabled:opacity-40"
        >
          {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Sync Products
        </button>
      </div>

      {/* Etsy Publishing */}
      <div className="mt-6 border border-white/10 bg-card p-6" data-testid="etsy-panel">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="font-bold uppercase tracking-tight text-white flex items-center gap-2" data-testid="etsy-status">
              <ShoppingBag className="w-5 h-5 text-primary" /> eBay Publishing
            </p>
            <p className="text-xs text-white/50 mt-1">
              {etsy ? `${etsy.published_count} of ${etsy.total_listings_if_all} listings published · ${etsy.unique_designs} designs` : "Loading…"}
            </p>
            <p className="text-[11px] text-white/40 mt-2 max-w-lg leading-relaxed">
              Before bulk publishing works, your eBay account must be ready: create 1 manual listing on eBay.com (anti-fraud), keep Printify's auto Payment/Shipping/Return policies, and raise your eBay selling limit above {etsy?.total_listings_if_all || 230}. Then run "Publish Test" — if it appears on eBay, click "Publish All".
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              data-testid="etsy-test-button"
              onClick={etsyTest}
              disabled={etsyBusy || etsy?.job?.status === "running"}
              className="border border-white/20 text-white hover:border-primary hover:text-primary px-5 py-3 font-bold uppercase tracking-[0.15em] text-xs transition-colors disabled:opacity-40"
            >
              Publish Test (1)
            </button>
            <button
              data-testid="etsy-publish-all-button"
              onClick={etsyPublishAll}
              disabled={etsyBusy || etsy?.job?.status === "running"}
              className="bg-primary text-black hover:bg-white px-5 py-3 font-bold uppercase tracking-[0.15em] text-xs transition-colors flex items-center gap-2 disabled:opacity-40"
            >
              {etsy?.job?.status === "running" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
              Publish All to eBay
            </button>
          </div>
        </div>
        {etsy?.job && (
          <div className="mt-4" data-testid="etsy-progress">
            <div className="flex justify-between text-[11px] uppercase tracking-widest text-white/50 mb-1">
              <span>{etsy.job.status}</span>
              <span>{etsy.job.done || 0}/{etsy.job.total || 0}{etsy.job.failed ? ` · ${etsy.job.failed} failed` : ""}</span>
            </div>
            <div className="h-2 bg-white/10 overflow-hidden rounded">
              <div className="h-full bg-primary transition-all" style={{ width: `${etsy.job.total ? Math.round((etsy.job.done / etsy.job.total) * 100) : 0}%` }} />
            </div>
            {etsy.job.errors?.length > 0 && (
              <p className="text-[11px] text-amber-300/70 mt-2" data-testid="etsy-errors">
                Recent issue: {etsy.job.errors[etsy.job.errors.length - 1]}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Orders */}
      <div className="mt-12">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <h2 className="font-anton text-2xl uppercase tracking-tight text-white">Orders</h2>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input
                data-testid="order-search-input"
                value={orderSearch}
                onChange={(e) => setOrderSearch(e.target.value)}
                placeholder="Search name, email, order ID…"
                className="bg-transparent border border-white/20 focus:border-primary outline-none py-2 pl-9 pr-3 text-sm text-white placeholder:text-white/40 w-64 transition-colors"
              />
            </div>
            <button
              data-testid="refresh-orders-button"
              onClick={loadOrders}
              className="flex items-center gap-2 border border-white/20 text-white/70 hover:border-primary hover:text-primary px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button
              data-testid="reconcile-now-button"
              onClick={reconcileNow}
              disabled={reconciling}
              title="Re-check PayPal for charged orders and push any stuck order into production"
              className="flex items-center gap-2 bg-primary text-black hover:bg-white disabled:opacity-40 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] transition-colors active:scale-95"
            >
              {reconciling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Reconcile Now
            </button>
            <button
              data-testid="archive-test-orders-button"
              onClick={archiveTestOrders}
              disabled={cleaning}
              title="Archive all test, abandoned and refunded orders — hides them but keeps them recoverable under the Archived tab"
              className="flex items-center gap-2 border border-white/20 text-white/70 hover:border-primary hover:text-primary disabled:opacity-40 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] transition-colors"
            >
              {cleaning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Archive className="w-3.5 h-3.5" />} Archive test orders
            </button>
          </div>
        </div>

        {/* Status filter tabs */}
        <div className="flex gap-2 mb-6 flex-wrap" data-testid="status-filter">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`status-tab-${t.key}`}
              onClick={() => setStatusFilter(t.key)}
              className={`px-4 py-2 text-[11px] font-bold uppercase tracking-[0.2em] border transition-colors ${
                statusFilter === t.key
                  ? "bg-primary text-black border-primary"
                  : "bg-transparent text-white/60 border-white/15 hover:border-white/50 hover:text-white"
              }`}
            >
              {t.label} <span className="opacity-60">({t.count})</span>
            </button>
          ))}
        </div>

        {ordersLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : filteredOrders.length === 0 ? (
          <p data-testid="no-orders" className="text-white/40 uppercase tracking-[0.2em] text-sm py-8">
            {orders.length === 0 ? "No orders yet." : "No orders match your filters."}
          </p>
        ) : (
          <div className="border border-white/10 divide-y divide-white/10" data-testid="orders-list">
            {filteredOrders.map((o) => {
              const paid = o.payment_status === "paid";
              const refunded = o.status === "refunded" || o.payment_status === "refunded";
              const archived = !!o.archived;
              const notFulfilled = paid && !refunded && !o.printify_order_id;
              const perso = (o.line_items || []).find((li) => li.custom_name || li.custom_number);
              const returnPending = o.return_status === "requested";
              const RETURN_BADGE = { requested: "Return requested", approved: "Refunded", replaced: "Replacement sent", denied: "Return denied" };
              const RTYPE = { defective: "Damaged/defective", error: "Mfg error", size: "Wrong size", other: "Other" };
              return (
                <div key={o.id} data-testid={`order-row-${o.id}`} className="flex flex-wrap items-center justify-between gap-4 px-4 py-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-bold text-white uppercase tracking-tight">#{o.id.slice(0, 8)}</span>
                      {o.test_mode && (
                        <span
                          data-testid={`test-badge-${o.id}`}
                          className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 border text-primary border-primary/50"
                        >
                          <FlaskConical className="w-3 h-3" /> Test
                        </span>
                      )}
                      <span className={`text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-0.5 border ${
                        refunded ? "text-white/40 border-white/20"
                          : paid ? "text-primary border-primary/40" : "text-white/40 border-white/20"
                      }`}>
                        {refunded ? "Refunded" : o.status || o.payment_status}
                      </span>
                      {o.email && (
                        <span
                          data-testid={`email-status-${o.id}`}
                          className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 border ${
                            o.confirmation_email_sent ? "text-green-400 border-green-500/40" : "text-amber-400 border-amber-500/40"
                          }`}
                        >
                          <Mail className="w-3 h-3" />
                          {o.confirmation_email_sent ? "Email sent" : "Not sent"}
                        </span>
                      )}
                      {notFulfilled && (
                        <span
                          data-testid={`fulfill-status-${o.id}`}
                          className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 border text-red-400 border-red-500/50"
                        >
                          <XCircle className="w-3 h-3" /> Not ordered
                        </span>
                      )}
                      {archived && (
                        <span
                          data-testid={`archived-badge-${o.id}`}
                          className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 border text-white/50 border-white/20"
                        >
                          <Archive className="w-3 h-3" /> Archived
                        </span>
                      )}
                      {o.return_status && (
                        <span
                          data-testid={`return-badge-${o.id}`}
                          className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 border ${
                            returnPending ? "text-amber-400 border-amber-500/50" : "text-white/50 border-white/20"
                          }`}
                        >
                          <PackageX className="w-3 h-3" /> {RETURN_BADGE[o.return_status]}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-white/50 mt-1 truncate">
                      {o.customer || "—"} · {o.email || "no email"}
                      {perso && <span className="text-primary"> · {perso.custom_name}{perso.custom_number ? ` #${perso.custom_number}` : ""}</span>}
                    </p>
                    {returnPending && o.return_reason && (
                      <p data-testid={`return-reason-${o.id}`} className="text-[11px] text-amber-300/80 mt-0.5 truncate">
                        {RTYPE[o.return_reason_type] || "Claim"}: {o.return_reason}
                      </p>
                    )}
                    {returnPending && (o.return_photo_paths?.length || o.return_photo_path) && (
                      <div data-testid={`return-photos-${o.id}`} className="flex flex-wrap gap-1.5 mt-1.5">
                        {(o.return_photo_paths?.length ? o.return_photo_paths : [o.return_photo_path]).map((p, i) => (
                          <a
                            key={i}
                            href={`${process.env.REACT_APP_BACKEND_URL}/api/returns/photo/${p}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={`View damage photo ${i + 1}`}
                          >
                            <img
                              src={`${process.env.REACT_APP_BACKEND_URL}/api/returns/photo/${p}`}
                              alt={`Damage ${i + 1}`}
                              className="w-16 h-16 object-cover border border-amber-500/40 hover:border-primary"
                            />
                          </a>
                        ))}
                      </div>
                    )}
                    {o.printify_order_id && (
                      <p className="text-[11px] text-white/30 mt-0.5">Printify: {o.printify_order_id}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-bold text-white whitespace-nowrap">{formatPrice(o.total_price)}</span>
                    {notFulfilled && (
                      <button
                        data-testid={`fulfill-button-${o.id}`}
                        onClick={() => fulfillOrder(o.id)}
                        disabled={fulfilling === o.id}
                        className="flex items-center gap-2 border border-primary text-primary hover:bg-primary hover:text-black px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] transition-colors disabled:opacity-40"
                      >
                        {fulfilling === o.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Package className="w-3.5 h-3.5" />}
                        Fulfill order
                      </button>
                    )}
                    {o.email && !refunded && (
                      <button
                        data-testid={`resend-email-${o.id}`}
                        onClick={() => resendEmail(o.id)}
                        disabled={resending === o.id}
                        className="flex items-center gap-2 border border-white/20 text-white/70 hover:border-primary hover:text-primary px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] transition-colors disabled:opacity-40"
                      >
                        {resending === o.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                        {o.confirmation_email_sent ? "Resend" : "Send email"}
                      </button>
                    )}
                    {returnPending && (
                      <>
                        <button
                          data-testid={`return-replace-${o.id}`}
                          onClick={() => replaceReturn(o.id)}
                          disabled={refunding === o.id}
                          className="flex items-center gap-2 border border-primary text-primary hover:bg-primary hover:text-black px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] transition-colors disabled:opacity-40"
                        >
                          {refunding === o.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Package className="w-3.5 h-3.5" />}
                          Replace free
                        </button>
                        <button
                          data-testid={`return-approve-${o.id}`}
                          onClick={() => approveReturn(o.id)}
                          disabled={refunding === o.id}
                          className="flex items-center gap-2 border border-white/20 text-white/80 hover:border-primary hover:text-primary px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] transition-colors disabled:opacity-40"
                        >
                          {refunding === o.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                          Refund
                        </button>
                        <button
                          data-testid={`return-deny-${o.id}`}
                          onClick={() => denyReturn(o.id)}
                          disabled={refunding === o.id}
                          className="flex items-center gap-2 border border-white/20 text-white/70 hover:border-red-500 hover:text-red-400 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] transition-colors disabled:opacity-40"
                        >
                          <XCircle className="w-3.5 h-3.5" /> Deny
                        </button>
                      </>
                    )}
                    {paid && !refunded && (
                      <button
                        data-testid={`refund-button-${o.id}`}
                        onClick={() => refundOrder(o.id)}
                        disabled={refunding === o.id}
                        className="flex items-center gap-2 border border-red-500/40 text-red-400 hover:bg-red-500 hover:text-white px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] transition-colors disabled:opacity-40"
                      >
                        {refunding === o.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                        Refund
                      </button>
                    )}
                    {archived ? (
                      <button
                        data-testid={`restore-order-${o.id}`}
                        onClick={() => restoreOrder(o.id)}
                        disabled={archiving === o.id}
                        className="flex items-center gap-2 border border-white/20 text-white/70 hover:border-primary hover:text-primary px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] transition-colors disabled:opacity-40"
                      >
                        {archiving === o.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArchiveRestore className="w-3.5 h-3.5" />}
                        Restore
                      </button>
                    ) : (
                      <button
                        data-testid={`archive-order-${o.id}`}
                        onClick={() => archiveOrder(o.id)}
                        disabled={archiving === o.id}
                        title="Hide this order (recoverable from the Archived tab)"
                        className="flex items-center justify-center border border-white/15 text-white/40 hover:border-primary hover:text-primary w-9 h-9 transition-colors disabled:opacity-40"
                      >
                        {archiving === o.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Archive className="w-3.5 h-3.5" />}
                      </button>
                    )}
                    <button
                      data-testid={`delete-order-${o.id}`}
                      onClick={() => deleteOrder(o.id)}
                      disabled={deletingOrder === o.id}
                      title="Permanently delete this order"
                      className="flex items-center justify-center border border-white/15 text-white/40 hover:border-red-500 hover:text-red-400 w-9 h-9 transition-colors disabled:opacity-40"
                    >
                      {deletingOrder === o.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* New Drop Announcement */}
      <div className="mt-12" data-testid="announce-drop-section">
        <h2 className="font-anton text-2xl uppercase tracking-tight mb-2 text-white">Announce a New Drop</h2>
        <p className="text-white/50 text-sm mb-6">
          Email all <span className="text-primary font-bold" data-testid="subscriber-count">{subCount}</span> newsletter subscribers about a new team drop.
        </p>
        <div className="border border-white/10 bg-card p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-white/50">Feature a product (optional)</label>
              <select
                data-testid="drop-product-select"
                value={drop.product_id}
                onChange={(e) => selectDropProduct(e.target.value)}
                className="mt-1.5 w-full bg-neutral-900 border border-white/20 focus:border-primary outline-none p-3 text-white text-sm"
              >
                <option value="">— None —</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>{p.title}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-white/50">Headline</label>
              <input
                data-testid="drop-title-input"
                value={drop.title}
                onChange={(e) => setDrop((d) => ({ ...d, title: e.target.value }))}
                placeholder="e.g. Japan Crest just dropped"
                className="mt-1.5 w-full bg-transparent border border-white/20 focus:border-primary outline-none p-3 text-white text-sm"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-white/50">Message (optional)</label>
            <textarea
              data-testid="drop-message-input"
              value={drop.message}
              onChange={(e) => setDrop((d) => ({ ...d, message: e.target.value }))}
              rows={3}
              placeholder="A fresh drop just landed. Grab yours before it's gone…"
              className="mt-1.5 w-full bg-transparent border border-white/20 focus:border-primary outline-none p-3 text-white text-sm resize-none"
            />
          </div>
          {drop.image_url && (
            <img src={drop.image_url} alt="" data-testid="drop-image-preview" className="w-24 h-28 object-contain bg-neutral-900 border border-white/10" />
          )}
          <button
            data-testid="send-drop-button"
            onClick={sendDrop}
            disabled={sending || subCount === 0}
            className="bg-primary text-black hover:bg-white px-6 py-3 font-bold uppercase tracking-[0.2em] text-sm transition-colors flex items-center gap-2 disabled:opacity-40"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send to {subCount} subscribers
          </button>
        </div>
      </div>

      {/* Owner / Sample Order */}
      <div className="mt-12" data-testid="owner-order-section">
        <h2 className="font-anton text-2xl uppercase tracking-tight mb-2 text-white flex items-center gap-3">
          <Gift className="w-6 h-6 text-primary" /> Place an Order (Owner)
        </h2>
        <p className="text-white/50 text-sm mb-6">
          Order a personalized piece for yourself, a sample, or a gift — ships straight to Printify production. This does NOT go through PayPal; your Printify account is charged.
        </p>

        <div className="border border-white/10 bg-card p-6 space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-white/50">Product</label>
              <select
                data-testid="sample-product-select"
                value={sample.product_id}
                onChange={(e) => setSample((s) => ({ ...s, product_id: e.target.value, garment: "", size: "" }))}
                className="mt-1.5 w-full bg-neutral-900 border border-white/20 focus:border-primary outline-none p-3 text-white text-sm"
              >
                <option value="">Select a product…</option>
                {products.filter((p) => p.variants?.some((v) => v.is_enabled)).map((p) => (
                  <option key={p.id} value={p.id}>{p.title}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1">
                <label className="text-[10px] uppercase tracking-widest font-bold text-white/50">Garment</label>
                <select
                  data-testid="sample-garment-select"
                  value={sample.garment}
                  disabled={!sampleProduct}
                  onChange={(e) => setSample((s) => ({ ...s, garment: e.target.value, size: "" }))}
                  className="mt-1.5 w-full bg-neutral-900 border border-white/20 focus:border-primary outline-none p-3 text-white text-sm disabled:opacity-40"
                >
                  <option value="">—</option>
                  {sampleGarments.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div className="col-span-1">
                <label className="text-[10px] uppercase tracking-widest font-bold text-white/50">Size</label>
                <select
                  data-testid="sample-size-select"
                  value={sample.size}
                  disabled={!sample.garment}
                  onChange={(e) => setSample((s) => ({ ...s, size: e.target.value }))}
                  className="mt-1.5 w-full bg-neutral-900 border border-white/20 focus:border-primary outline-none p-3 text-white text-sm disabled:opacity-40"
                >
                  <option value="">—</option>
                  {sampleSizes.map((sz) => <option key={sz} value={sz}>{sz}</option>)}
                </select>
              </div>
              <div className="col-span-1">
                <label className="text-[10px] uppercase tracking-widest font-bold text-white/50">Qty</label>
                <input
                  data-testid="sample-qty-input"
                  type="number" min="1" max="10"
                  value={sample.quantity}
                  onChange={(e) => setSample((s) => ({ ...s, quantity: e.target.value }))}
                  className="mt-1.5 w-full bg-neutral-900 border border-white/20 focus:border-primary outline-none p-3 text-white text-sm"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-white/50">Name on back (optional)</label>
              <input
                data-testid="sample-name-input"
                value={sample.custom_name}
                onChange={(e) => setSample((s) => ({ ...s, custom_name: e.target.value.toUpperCase() }))}
                placeholder="e.g. SANTOS"
                className="mt-1.5 w-full bg-transparent border border-white/20 focus:border-primary outline-none p-3 text-white text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-white/50">Number on back (optional)</label>
              <input
                data-testid="sample-number-input"
                value={sample.custom_number}
                onChange={(e) => setSample((s) => ({ ...s, custom_number: e.target.value.replace(/[^0-9]/g, "") }))}
                placeholder="e.g. 10"
                className="mt-1.5 w-full bg-transparent border border-white/20 focus:border-primary outline-none p-3 text-white text-sm"
              />
            </div>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-primary mb-3">Back preview</p>
            <div className="max-w-[220px]" data-testid="sample-back-preview">
              <JerseyBackPreview name={sample.custom_name} number={sample.custom_number} garment={sample.garment} />
            </div>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-primary mb-3">Ship to</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                ["first_name", "First name"], ["last_name", "Last name"],
                ["email", "Email"], ["phone", "Phone (optional)"],
                ["address1", "Address line 1"], ["address2", "Address line 2 (optional)"],
                ["city", "City"], ["region", "State / Region"],
                ["zip", "ZIP / Postal code"], ["country", "Country code (e.g. US)"],
              ].map(([k, label]) => (
                <div key={k}>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-white/50">{label}</label>
                  <input
                    data-testid={`sample-${k}-input`}
                    value={sample[k]}
                    onChange={(e) => setSample((s) => ({ ...s, [k]: e.target.value }))}
                    className="mt-1.5 w-full bg-transparent border border-white/20 focus:border-primary outline-none p-3 text-white text-sm"
                  />
                </div>
              ))}
            </div>
          </div>

          <button
            data-testid="place-sample-order-button"
            onClick={placeSampleOrder}
            disabled={placingSample}
            className="bg-primary text-black hover:bg-white px-6 py-3 font-bold uppercase tracking-[0.2em] text-sm transition-colors flex items-center gap-2 disabled:opacity-40"
          >
            {placingSample ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
            Place order to production
          </button>
        </div>
      </div>


      {/* Scheduled Sales */}
      <div className="mt-12" data-testid="scheduled-sales-section">
        <h2 className="font-anton text-2xl uppercase tracking-tight mb-2 text-white flex items-center gap-3">
          <CalendarClock className="w-6 h-6 text-primary" /> Scheduled Sales
        </h2>
        <p className="text-white/50 text-sm mb-6">
          Queue store-wide discounts by date. Each sale automatically turns on at its start date and off after its end date — no redeploy needed.
        </p>

        <form onSubmit={createSale} className="border border-white/10 bg-card p-6 space-y-4" data-testid="sale-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-white/50">Sale name</label>
              <input
                data-testid="sale-name-input"
                value={newSale.name}
                onChange={(e) => setNewSale((s) => ({ ...s, name: e.target.value }))}
                placeholder="e.g. Black Friday, Summer Sale"
                className="mt-1.5 w-full bg-transparent border border-white/20 focus:border-primary outline-none p-3 text-white text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-white/50">Discount ($ off each item)</label>
              <input
                data-testid="sale-discount-input"
                type="number"
                min="1"
                step="1"
                value={newSale.discount_dollars}
                onChange={(e) => setNewSale((s) => ({ ...s, discount_dollars: e.target.value }))}
                placeholder="5"
                className="mt-1.5 w-full bg-transparent border border-white/20 focus:border-primary outline-none p-3 text-white text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-white/50">Start date</label>
              <input
                data-testid="sale-start-input"
                type="date"
                value={newSale.start}
                onChange={(e) => setNewSale((s) => ({ ...s, start: e.target.value }))}
                className="mt-1.5 w-full bg-transparent border border-white/20 focus:border-primary outline-none p-3 text-white text-sm [color-scheme:dark]"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-white/50">End date (inclusive)</label>
              <input
                data-testid="sale-end-input"
                type="date"
                value={newSale.end}
                onChange={(e) => setNewSale((s) => ({ ...s, end: e.target.value }))}
                className="mt-1.5 w-full bg-transparent border border-white/20 focus:border-primary outline-none p-3 text-white text-sm [color-scheme:dark]"
              />
            </div>
          </div>
          <button
            data-testid="create-sale-button"
            type="submit"
            disabled={creatingSale}
            className="bg-primary text-black hover:bg-white px-6 py-3 font-bold uppercase tracking-[0.2em] text-sm transition-colors flex items-center gap-2 disabled:opacity-40"
          >
            {creatingSale ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Schedule sale
          </button>
        </form>

        <div className="mt-6">
          {salesLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : sales.length === 0 ? (
            <p data-testid="no-sales" className="text-white/40 uppercase tracking-[0.2em] text-sm py-6">No sales scheduled yet.</p>
          ) : (
            <div className="border border-white/10 divide-y divide-white/10" data-testid="sales-list">
              {sales.map((s) => (
                <div key={s.id} data-testid={`sale-row-${s.id}`} className="flex flex-wrap items-center justify-between gap-4 px-4 py-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <Tag className="w-4 h-4 text-primary shrink-0" />
                      <span className="font-bold text-white uppercase tracking-tight">{s.name}</span>
                      <span
                        data-testid={`sale-badge-${s.id}`}
                        className={`text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-0.5 border ${
                          s.active ? "text-primary border-primary/50"
                            : s.upcoming ? "text-amber-400 border-amber-500/40"
                            : "text-white/40 border-white/20"
                        }`}
                      >
                        {s.active ? "Active now" : s.upcoming ? "Upcoming" : "Ended"}
                      </span>
                    </div>
                    <p className="text-xs text-white/50 mt-1">
                      {formatPrice(s.discount_cents)} off each item · {s.start} → {s.end}
                    </p>
                  </div>
                  <button
                    data-testid={`delete-sale-${s.id}`}
                    onClick={() => deleteSale(s.id)}
                    disabled={deletingSale === s.id}
                    className="flex items-center gap-2 border border-red-500/40 text-red-400 hover:bg-red-500 hover:text-white px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] transition-colors disabled:opacity-40"
                  >
                    {deletingSale === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Categories */}
      <div className="mt-12">
        <h2 className="font-anton text-2xl uppercase tracking-tight mb-6 text-white">Categories</h2>
        <form onSubmit={addCat} className="flex gap-3 mb-6">
          <input
            data-testid="new-category-input"
            value={newCat}
            onChange={(e) => setNewCat(e.target.value)}
            placeholder="e.g. Anime, Gaming, Music..."
            className="flex-1 bg-transparent border border-white/20 focus:border-primary outline-none p-3.5 text-white placeholder:text-white/40"
          />
          <button
            data-testid="add-category-button"
            type="submit"
            className="bg-primary text-black hover:bg-white px-6 font-bold uppercase tracking-[0.2em] text-sm transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add
          </button>
        </form>
        <div className="divide-y divide-white/10 border border-white/10">
          {categories.map((c) => (
            <div key={c.id} data-testid="category-row" className="flex items-center justify-between px-4 py-3">
              <span className="font-bold uppercase tracking-tight text-white">{c.name} <span className="text-white/30 text-xs font-normal">/{c.slug}</span></span>
              <button onClick={() => delCat(c.id)} className="text-white/30 hover:text-primary" data-testid={`delete-category-${c.slug}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <p className="text-xs text-white/40 mt-4">
          Tip: To place a Printify product into a category, add a matching tag (e.g. "music") to it in Printify, then hit Sync.
        </p>
      </div>
    </div>
  );
}
